import { useTranslate } from "ra-core";
import { createRef, useCallback, useRef, useState } from "react";
import type { ReactCropperElement } from "react-cropper";
import { Cropper } from "react-cropper";
import { useDropzone } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import "cropperjs/dist/cropper.css";

const ImageEditorField = (props: ImageEditorFieldProps) =>
  props.preserveOriginal ? (
    <OriginalImageField {...props} />
  ) : (
    <CroppedImageField {...props} />
  );

const OriginalImageField = (props: ImageEditorFieldProps) => {
  const translate = useTranslate();
  const { setValue, handleSubmit } = useFormContext();
  const value = useWatch({ name: props.source });
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        className="rounded-lg border p-4"
        style={{ backgroundColor: props.backgroundImageColor }}
        aria-label={translate("crm.image_editor.change")}
        onClick={() => input.current?.click()}
      >
        <img
          src={value?.src}
          alt={translate("crm.image_editor.editable_content")}
          className="object-contain"
          style={{ width: props.width ?? 240, height: props.height ?? 80 }}
        />
      </button>
      <input
        ref={input}
        type="file"
        className="hidden"
        aria-label={
          props.source === "lightModeLogo"
            ? "Логотип для светлой темы"
            : "Логотип для тёмной темы"
        }
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setError("");
          const reader = new FileReader();
          reader.onerror = () => setError("Не удалось прочитать изображение");
          reader.onload = () => {
            setValue(
              props.source,
              { src: String(reader.result), title: file.name, rawFile: file },
              { shouldDirty: true },
            );
            if (props.onSave) handleSubmit(props.onSave)();
          };
          reader.readAsDataURL(file);
          event.target.value = "";
        }}
      />
      <button
        type="button"
        className="text-xs underline"
        onClick={() => input.current?.click()}
      >
        {translate("crm.image_editor.change")}
      </button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};

const CroppedImageField = (props: ImageEditorFieldProps) => {
  const translate = useTranslate();
  const source = useWatch({ name: props.source });
  const imageUrl = source?.src;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { type = "image", emptyText, linkPosition = "none" } = props;

  const commonProps = {
    src: imageUrl,
    onClick: () => setIsDialogOpen(true),
    style: { cursor: "pointer" },
    className: `${props.className || ""}`,
  };

  const width = props.width || (type === "avatar" ? 50 : 200);
  const height = props.height || (type === "avatar" ? 50 : 200);

  return (
    <>
      <div
        className={`flex ${
          linkPosition === "right" ? "flex-row" : "flex-col"
        } items-center ${linkPosition === "right" ? "gap-2" : "gap-1"}`}
      >
        <div
          className={`rounded ${props.backgroundImageColor ? "p-4" : "p-0"}`}
          style={{
            backgroundColor: props.backgroundImageColor || "transparent",
          }}
        >
          {props.type === "avatar" ? (
            <Avatar
              {...commonProps}
              className={`cursor-pointer`}
              style={{ width, height }}
            >
              <AvatarImage src={imageUrl} />
              <AvatarFallback>{emptyText}</AvatarFallback>
            </Avatar>
          ) : (
            <img
              {...commonProps}
              className={
                props.preserveOriginal
                  ? "cursor-pointer object-contain"
                  : "cursor-pointer object-cover"
              }
              style={{ width, height }}
              alt={translate("crm.image_editor.editable_content", {
                _: "Editable content",
              })}
            />
          )}
        </div>
        {linkPosition !== "none" && (
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            data-mobile-control="text"
            className="text-xs px-3 py-2 cursor-pointer text-center"
          >
            {translate("crm.image_editor.change")}
          </button>
        )}
      </div>
      <ImageEditorDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        {...props}
      />
    </>
  );
};

const ImageEditorDialog = (props: ImageEditorDialogProps) => {
  const translate = useTranslate();
  const { setValue, handleSubmit } = useFormContext();
  const cropperRef = createRef<ReactCropperElement>();
  const initialValue = useWatch({ name: props.source });
  const [file, setFile] = useState<File | undefined>();
  const [imageSrc, setImageSrc] = useState<string | undefined>(
    initialValue?.src,
  );
  const onDrop = useCallback(
    (files: File[]) => {
      if (!files[0]) return;
      if (props.preserveOriginal) {
        setFile(files[0]);
        const reader = new FileReader();
        reader.onload = () => setImageSrc(String(reader.result));
        reader.readAsDataURL(files[0]);
        return;
      }
      const preview = URL.createObjectURL(files[0]);
      setFile(files[0]);
      setImageSrc(preview);
    },
    [props.preserveOriginal],
  );

  const updateImage = () => {
    const cropper = cropperRef.current?.cropper;
    const croppedImage = props.preserveOriginal
      ? imageSrc
      : cropper
          ?.getCroppedCanvas({ maxWidth: 512, maxHeight: 512 })
          .toDataURL("image/webp", 0.85);
    if (croppedImage) {
      setImageSrc(croppedImage);

      const newFile = file ?? new File([], initialValue?.src);
      setValue(
        props.source,
        {
          src: croppedImage,
          title: newFile.name,
          ...(!props.preserveOriginal || file ? { rawFile: newFile } : {}),
        },
        { shouldDirty: true },
      );
      props.onClose();

      if (props.onSave) {
        handleSubmit(props.onSave)();
      }
    }
  };

  const deleteImage = () => {
    setValue(props.source, null, { shouldDirty: true });
    if (props.onSave) {
      handleSubmit(props.onSave)();
    }
    setImageSrc(undefined);
    props.onClose();
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      ...(props.preserveOriginal ? { "image/svg+xml": [".svg"] } : {}),
    },
    onDrop,
    maxFiles: 1,
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onClose}>
      {props.type === "avatar" && (
        <style>
          {`
                        .cropper-crop-box,
                        .cropper-view-box {
                            border-radius: 50%;
                        }
                    `}
        </style>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.image_editor.title", {
              _: "Upload and resize image",
            })}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 justify-center">
          <div
            className="flex flex-row justify-center bg-muted cursor-pointer p-4 border-2 border-dashed border-border rounded-lg hover:bg-accent transition-colors"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <p className="text-muted-foreground">
              {translate("crm.image_editor.drop_hint", {
                _: "Drop a file to upload, or click to select it.",
              })}
            </p>
          </div>

          {imageSrc && props.preserveOriginal ? (
            <img
              src={imageSrc}
              alt="Предпросмотр логотипа"
              className="h-40 w-full object-contain rounded-lg border bg-card p-4"
            />
          ) : (
            imageSrc && (
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                aspectRatio={1}
                guides={false}
                cropBoxResizable={false}
              />
            )
          )}
        </div>

        <DialogFooter className="flex justify-between w-full">
          <Button type="button" onClick={updateImage}>
            {translate("crm.image_editor.update_image")}
          </Button>
          <Button type="button" variant="destructive" onClick={deleteImage}>
            {translate("ra.action.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditorField;

export interface ImageEditorFieldProps {
  source: string;
  preserveOriginal?: boolean;
  width?: number;
  height?: number;
  type?: "avatar" | "image";
  onSave?: any;
  linkPosition?: "right" | "bottom" | "none";
  backgroundImageColor?: string;
  className?: string;
  emptyText?: string;
}

export interface ImageEditorDialogProps extends ImageEditorFieldProps {
  open: boolean;
  onClose: () => void;
}
